import Phaser from "phaser";
import type { AnimState } from "../../art";
import type { CardinalDir } from "../../art/types";
import { spawnMoveDust } from "../../vfx/move-dust";
import {
  attackMotion,
  bodyBobY,
  footPhaseOffsets,
  footSideFromAim,
  gripLocalPos,
  moveDustScaleFromDisplay,
  muzzleLocal,
  propScaleFromBodyDisplay,
  resolveRigConfig,
  shadowEllipseFromDisplay,
  stepDisplayAim,
  trailSpawnChance,
  weaponDrawnBehindBody,
  type AttackStyle,
  type RigRoleConfig,
  type RigTables,
  type ShieldStyle,
  type WeaponShape,
  type WeaponSkin,
} from "./unit-rig-math";
import {
  resolveGripLocal,
  type GripPack,
} from "./grip-resolve";

export type {
  AttackStyle,
  AttackMotion,
  RigRoleConfig,
  RigTables,
  ShieldStyle,
  WeaponShape,
  WeaponSkin,
} from "./unit-rig-math";
export {
  attackMotion,
  bodyBobY,
  footBiasFromDisplay,
  footPhaseOffsets,
  footSideFromAim,
  gripLocalPos,
  MOVE_DUST_TRAIL_CD_SEC,
  moveDustScaleFromDisplay,
  muzzleLocal,
  propScaleFromBodyDisplay,
  resolveRigConfig,
  shadowEllipseFromDisplay,
  stepDisplayAim,
  trailSpawnChance,
  weaponDrawnBehindBody,
  weaponSwingOffset,
} from "./unit-rig-math";

export type UnitRigUpdate = {
  aimAngle: number;
  animState: AnimState;
  animTime: number;
  intentVx: number;
  intentVy: number;
  facing: number;
  dying: boolean;
  /** When true, body texture is already directional (e.g. W pre-flipped) — do not flipX. */
  lockBodyFlip?: boolean;
  /** Cardinal facing for grip SSOT (Dropfront authored dirs). */
  facingDir?: CardinalDir;
};

type TrailBudget = { left: number };

function shadeRgb(color: number, mul: number): number {
  const r = Math.min(255, Math.max(0, Math.round(((color >> 16) & 255) * mul)));
  const g = Math.min(255, Math.max(0, Math.round(((color >> 8) & 255) * mul)));
  const b = Math.min(255, Math.max(0, Math.round((color & 255) * mul)));
  return (r << 16) | (g << 8) | b;
}

function addGlowRect(
  scene: Phaser.Scene,
  parts: Phaser.GameObjects.GameObject[],
  x: number,
  y: number,
  w: number,
  h: number,
  color: number,
  glow: boolean,
): void {
  if (!glow) return;
  parts.push(
    scene.add.rectangle(x, y, w + 4, h + 4, color, 0.22).setOrigin(0, 0.5),
  );
}

/** Multi-tone shaft: lo underlay + mid body + hi edge strip. */
function addShaft(
  scene: Phaser.Scene,
  parts: Phaser.GameObjects.GameObject[],
  x: number,
  y: number,
  len: number,
  thick: number,
  color: number,
  glow: boolean,
): void {
  addGlowRect(scene, parts, x, y, len, thick, color, glow);
  parts.push(
    scene.add
      .rectangle(x, y, len, thick, shadeRgb(color, 0.55), 1)
      .setOrigin(0, 0.5),
    scene.add
      .rectangle(x, y, len, thick * 0.72, color, 1)
      .setOrigin(0, 0.5),
    scene.add
      .rectangle(x + 1, y - thick * 0.22, len * 0.85, Math.max(1.2, thick * 0.28), 0xffffff, 0.35)
      .setOrigin(0, 0.5),
  );
}

/** Wood / leather wrap band on a shaft. */
function addWrapBand(
  scene: Phaser.Scene,
  parts: Phaser.GameObjects.GameObject[],
  x: number,
  y: number,
  w: number,
  h: number,
  color: number,
): void {
  parts.push(
    scene.add.rectangle(x, y, w, h, shadeRgb(color, 0.55), 1).setOrigin(0, 0.5),
    scene.add.rectangle(x, y - h * 0.2, w, h * 0.45, color, 1).setOrigin(0, 0.5),
  );
}

/** Tapered steel blade via Graphics (lo edge + mid + hi bevel + tip). */
function addTaperBlade(
  scene: Phaser.Scene,
  parts: Phaser.GameObjects.GameObject[],
  x0: number,
  len: number,
  halfW: number,
  color: number,
  glow: boolean,
): void {
  const tipX = x0 + len;
  const g = scene.add.graphics();
  if (glow) {
    g.fillStyle(color, 0.18);
    g.fillTriangle(x0 - 1, -halfW - 2, x0 - 1, halfW + 2, tipX + 3, 0);
  }
  // Lo rim (selout)
  g.fillStyle(shadeRgb(color, 0.35), 1);
  g.fillTriangle(x0, -halfW, x0, halfW, tipX + 1, 0);
  // Mid plate
  g.fillStyle(shadeRgb(color, 0.72), 1);
  g.fillTriangle(x0, -halfW * 0.78, x0, halfW * 0.78, tipX, 0);
  // Lit face
  g.fillStyle(color, 1);
  g.fillTriangle(x0, -halfW * 0.55, x0, halfW * 0.18, tipX - 1, 0);
  // Specular bevel strip
  g.fillStyle(0xffffff, 0.42);
  g.fillTriangle(x0 + 1, -halfW * 0.48, x0 + 1, -halfW * 0.12, tipX - len * 0.22, -0.8);
  // Fuller groove
  g.lineStyle(1.2, shadeRgb(color, 0.45), 0.85);
  g.lineBetween(x0 + 2, 0.4, tipX - 4, 0.4);
  parts.push(g);
}

function buildWeaponParts(
  scene: Phaser.Scene,
  shape: WeaponShape,
  len: number,
  color: number,
  glow: boolean,
  skin: WeaponSkin = "default",
): Phaser.GameObjects.GameObject[] {
  const parts: Phaser.GameObjects.GameObject[] = [];
  const L = Math.max(10, len);
  const holy = skin === "holy";
  const cursed = skin === "cursed";

  switch (shape) {
    case "spear": {
      if (holy) {
        parts.push(
          scene.add.rectangle(1, 0, 5, 5, 0x3a3020, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(1, 0, 6, 2.5, 0xc8a050, 1).setOrigin(0.5, 0.5),
        );
        addShaft(scene, parts, 4, 0, L, 4.2, 0xe8c878, glow);
        const cx = 4 + L * 0.72;
        parts.push(
          scene.add.triangle(cx, 0, 0, -6, 8, 0, 0, 6, 0xffe08a),
          scene.add.triangle(cx, 0, 0, 6, 8, 0, 0, -6, 0xfff6c8),
          scene.add.rectangle(cx + 2, 0, 4, 2, 0xffffff, 0.7).setOrigin(0, 0.5),
        );
        const tip = 4 + L + 5;
        parts.push(
          scene.add.triangle(tip, 0, -3, -4.5, 8, 0, -3, 4.5, shadeRgb(color, 0.85)),
          scene.add.triangle(tip, 0, -2, -2.8, 6, 0, -2, 2.8, 0xffffff),
          scene.add.circle(tip + 5, 0, 1.8, 0x7ce8ff, 0.9),
        );
      } else if (cursed) {
        const g = scene.add.graphics();
        g.fillStyle(0x2a2420, 1);
        g.fillEllipse(1, 0, 6, 6);
        for (let i = 0; i < 5; i++) {
          const x = 4 + i * (L / 5);
          g.fillStyle(i % 2 ? 0xd8d0c0 : 0xb8b0a0, 1);
          g.fillRoundedRect(x, -2.4, L / 5.2, 4.8, 1.5);
          g.fillStyle(0xf0e8d8, 0.55);
          g.fillRect(x + 1, -1.8, L / 6, 1.4);
        }
        const hx = 4 + L * 0.78;
        g.fillStyle(0xe8e0d0, 1);
        g.fillEllipse(hx, 0, 9, 8);
        g.fillStyle(0x2a2030, 1);
        g.fillCircle(hx - 1.5, -1.2, 1.6);
        g.fillCircle(hx + 1.8, -1.0, 1.6);
        g.fillStyle(0x1a1520, 1);
        g.fillEllipse(hx + 0.2, 1.8, 3.5, 2.2);
        const tip = 4 + L + 4;
        g.fillStyle(shadeRgb(color, 0.7), 1);
        g.fillTriangle(tip - 2, -3.5, tip + 9, 0, tip - 2, 3.5);
        g.fillStyle(0xf8f0e0, 1);
        g.fillTriangle(tip, -2, tip + 7, 0, tip, 2);
        if (glow) {
          g.fillStyle(color, 0.18);
          g.fillCircle(tip + 2, 0, 7);
        }
        parts.push(g);
      } else {
        parts.push(
          scene.add.rectangle(1, 0, 5, 5, 0x2a2a35, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(1, 0, 6, 2, 0x6a6048, 1).setOrigin(0.5, 0.5),
        );
        addShaft(scene, parts, 4, 0, L, 4.5, color, glow);
        parts.push(
          scene.add.circle(4 + L + 4, 0, 4.5, shadeRgb(color, 0.7), 1),
          scene.add.circle(4 + L + 4, 0, 3.2, 0xd0d8e8, 1),
          scene.add.circle(4 + L + 5.5, 0, 1.6, 0xffffff, 0.7),
        );
      }
      break;
    }
    case "mace": {
      if (holy) {
        parts.push(
          scene.add.rectangle(1, 0, 5, 5, 0x3a3020, 1).setOrigin(0.5, 0.5),
        );
        addShaft(scene, parts, 4, 0, L * 0.55, 4.0, 0xe0c070, glow);
        const hx = 4 + L * 0.55 + 7;
        parts.push(
          scene.add.circle(hx, 0, 8.5, shadeRgb(color, 0.5), 1),
          scene.add.rectangle(hx, 0, 14, 4.5, color, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(hx, 0, 4.5, 14, color, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(hx, 0, 12, 2.8, 0xfff6c8, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(hx, 0, 2.8, 12, 0xfff6c8, 1).setOrigin(0.5, 0.5),
          scene.add.circle(hx, 0, 3.2, 0xffffff, 0.85),
          scene.add.circle(hx - 1.2, -1.2, 1.4, 0x7ce8ff, 0.7),
        );
      } else if (cursed) {
        parts.push(
          scene.add.rectangle(1, 0, 5, 5, 0x2a2030, 1).setOrigin(0.5, 0.5),
        );
        addShaft(scene, parts, 4, 0, L * 0.55, 4.0, 0x4a3858, glow);
        const hx = 4 + L * 0.55 + 6;
        const g = scene.add.graphics();
        g.fillStyle(shadeRgb(color, 0.45), 1);
        g.fillCircle(hx, 0, 8);
        for (const a of [-0.6, 0, 0.6, 1.2, -1.2, 2.0]) {
          g.fillStyle(shadeRgb(color, 0.7), 1);
          g.fillTriangle(
            hx + Math.cos(a) * 4,
            Math.sin(a) * 4,
            hx + Math.cos(a) * 10,
            Math.sin(a) * 10 - 2,
            hx + Math.cos(a) * 10,
            Math.sin(a) * 10 + 2,
          );
        }
        g.fillStyle(0x1a1028, 1);
        g.fillCircle(hx, 0, 3);
        parts.push(g);
      } else {
        parts.push(
          scene.add.rectangle(1, 0, 5, 5, 0x2a2a35, 1).setOrigin(0.5, 0.5),
        );
        addShaft(scene, parts, 4, 0, L * 0.55, 4.2, 0x6a7080, glow);
        const hx = 4 + L * 0.55 + 6;
        parts.push(
          scene.add.circle(hx, 0, 7.5, shadeRgb(color, 0.55), 1),
          scene.add.circle(hx, 0, 6, color, 1),
          scene.add.circle(hx - 1.5, -1.5, 2.8, 0xffffff, 0.4),
          scene.add.circle(hx + 2, 2, 1.8, shadeRgb(color, 0.4), 1),
        );
      }
      break;
    }
    case "scythe": {
      const g = scene.add.graphics();
      const pole = cursed ? 0x3a2a40 : 0x4a4038;
      const poleHi = cursed ? 0x6a5080 : 0x7a6850;
      g.fillStyle(shadeRgb(pole, 0.7), 1);
      g.fillRect(2, -2.2, L * 0.92, 4.4);
      g.fillStyle(pole, 1);
      g.fillRect(2, -1.5, L * 0.92, 3);
      g.fillStyle(poleHi, 0.75);
      g.fillRect(2, -1.3, L * 0.92, 1.1);
      g.fillStyle(0x2a2030, 1);
      g.fillRect(2 + L * 0.12, -3, 6, 6);
      const bx = 2 + L * 0.78;
      const blade = color;
      g.fillStyle(shadeRgb(blade, 0.4), 1);
      g.beginPath();
      g.moveTo(bx, -2);
      g.lineTo(bx + 6, -14);
      g.lineTo(bx + 18, -10);
      g.lineTo(bx + 14, -2);
      g.lineTo(bx + 20, 4);
      g.lineTo(bx + 8, 2);
      g.closePath();
      g.fillPath();
      g.fillStyle(blade, 1);
      g.beginPath();
      g.moveTo(bx + 1, -1);
      g.lineTo(bx + 7, -11);
      g.lineTo(bx + 15, -8);
      g.lineTo(bx + 12, -1);
      g.lineTo(bx + 16, 2.5);
      g.lineTo(bx + 7, 1);
      g.closePath();
      g.fillPath();
      g.fillStyle(0xe8d8ff, 0.65);
      g.beginPath();
      g.moveTo(bx + 3, -2);
      g.lineTo(bx + 8, -9);
      g.lineTo(bx + 12, -6);
      g.lineTo(bx + 6, -1);
      g.closePath();
      g.fillPath();
      if (glow) {
        g.fillStyle(blade, 0.2);
        g.fillCircle(bx + 10, -5, 9);
      }
      g.fillStyle(0xd0c8b8, 1);
      g.fillEllipse(bx - 1, 0, 6, 5);
      g.fillStyle(0x1a1020, 1);
      g.fillCircle(bx - 2, -0.8, 1.1);
      g.fillCircle(bx + 0.5, -0.6, 1.1);
      parts.push(g);
      break;
    }
    case "cleaver": {
      if (cursed) {
        parts.push(
          scene.add.rectangle(1, 0, 5, 5, 0x2a2030, 1).setOrigin(0.5, 0.5),
        );
        const g = scene.add.graphics();
        g.fillStyle(0x3a3038, 1);
        g.fillRect(3, -2.2, L * 0.28, 4.4);
        g.fillStyle(shadeRgb(color, 0.45), 1);
        g.fillTriangle(3 + L * 0.25, -7, 3 + L, -2, 3 + L * 0.25, 7);
        g.fillStyle(color, 1);
        g.fillTriangle(3 + L * 0.3, -5, 3 + L * 0.92, -1, 3 + L * 0.3, 5);
        g.fillStyle(0xd8c8ff, 0.5);
        g.fillTriangle(3 + L * 0.35, -3.5, 3 + L * 0.75, -1.5, 3 + L * 0.35, 0.5);
        for (let i = 0; i < 4; i++) {
          const x = 3 + L * (0.4 + i * 0.12);
          g.fillStyle(0x1a1028, 1);
          g.fillTriangle(x, 3, x + 3, 7, x + 5, 3);
        }
        if (glow) {
          g.fillStyle(color, 0.18);
          g.fillCircle(3 + L * 0.6, 0, 8);
        }
        parts.push(g);
      } else {
        parts.push(
          scene.add.rectangle(1, 0, 5, 5, 0x2a2a35, 1).setOrigin(0.5, 0.5),
        );
        addGlowRect(scene, parts, 3, 0, L, 10, color, glow);
        parts.push(
          scene.add.rectangle(3, 0, L * 0.35, 4.5, 0x3a3030, 1).setOrigin(0, 0.5),
          scene.add
            .rectangle(3 + L * 0.28, 0, L * 0.72, 9, shadeRgb(color, 0.65), 1)
            .setOrigin(0, 0.5),
          scene.add
            .rectangle(3 + L * 0.32, -1, L * 0.65, 6.5, color, 1)
            .setOrigin(0, 0.5),
          scene.add
            .rectangle(3 + L * 0.38, -3.2, L * 0.5, 2.5, 0xffc9a8, 0.55)
            .setOrigin(0, 0.5),
        );
      }
      break;
    }
    case "bow": {
      if (holy) {
        const g = scene.add.graphics();
        const r = L * 0.5;
        g.lineStyle(5.5, 0xa88840, 1);
        g.beginPath();
        g.arc(7, 0, r + 0.5, -1.25, 1.25, false);
        g.strokePath();
        g.lineStyle(4.0, 0xf0e0a8, 1);
        g.beginPath();
        g.arc(7, 0, r, -1.2, 1.2, false);
        g.strokePath();
        g.lineStyle(2.4, 0xffffff, 0.95);
        g.beginPath();
        g.arc(6.2, -0.6, r * 0.92, -1.05, -0.15, false);
        g.strokePath();
        g.lineStyle(3.2, 0x7ce8ff, 1);
        g.beginPath();
        g.arc(7, 0, r + 0.2, -1.28, -1.05, false);
        g.strokePath();
        g.beginPath();
        g.arc(7, 0, r + 0.2, 1.05, 1.28, false);
        g.strokePath();
        g.fillStyle(0xc8a050, 1);
        g.fillRect(4, -3.2, 6, 6.4);
        g.fillStyle(0xffe08a, 1);
        g.fillRect(5, -2.4, 4, 4.8);
        g.lineStyle(1.6, 0xa8fff0, 0.95);
        g.lineBetween(7, -r * 0.92, 7, r * 0.92);
        parts.push(g);
        if (glow) {
          parts.push(scene.add.circle(8 + L * 0.15, 0, 5, color, 0.2));
        }
        const ax = 9;
        parts.push(
          scene.add.rectangle(ax, 0, L * 0.55, 2.0, 0xf0e8c8, 1).setOrigin(0, 0.5),
          scene.add
            .rectangle(ax, -0.7, L * 0.48, 0.9, 0xffffff, 0.8)
            .setOrigin(0, 0.5),
          scene.add.triangle(
            ax + L * 0.55,
            0,
            5,
            0,
            0,
            -2.5,
            0,
            2.5,
            0x7ce8ff,
          ),
          scene.add.rectangle(ax - 1, -2.2, 3.5, 1.4, 0xffe08a, 0.95).setOrigin(0.5, 0.5),
          scene.add.rectangle(ax - 1, 2.2, 3.5, 1.4, 0xffe08a, 0.95).setOrigin(0.5, 0.5),
        );
      } else {
        const woodLo = 0x4a3428;
        const woodMid = 0x7a5840;
        const woodHi = 0xb88858;
        const horn = 0xd8c090;
        const g = scene.add.graphics();
        const r = L * 0.5;
        g.lineStyle(5.5, woodLo, 1);
        g.beginPath();
        g.arc(7, 0, r + 0.5, -1.25, 1.25, false);
        g.strokePath();
        g.lineStyle(4.0, woodMid, 1);
        g.beginPath();
        g.arc(7, 0, r, -1.2, 1.2, false);
        g.strokePath();
        g.lineStyle(2.2, woodHi, 0.95);
        g.beginPath();
        g.arc(6.2, -0.6, r * 0.92, -1.05, -0.15, false);
        g.strokePath();
        g.lineStyle(3.2, horn, 1);
        g.beginPath();
        g.arc(7, 0, r + 0.2, -1.28, -1.05, false);
        g.strokePath();
        g.beginPath();
        g.arc(7, 0, r + 0.2, 1.05, 1.28, false);
        g.strokePath();
        g.fillStyle(0x3a2a20, 1);
        g.fillRect(4, -3.2, 6, 6.4);
        g.fillStyle(0x6a4838, 1);
        g.fillRect(5, -2.4, 4, 4.8);
        g.lineStyle(1.4, 0xe8e0c8, 0.9);
        g.lineBetween(7, -r * 0.92, 7, r * 0.92);
        parts.push(g);
        if (glow) {
          parts.push(scene.add.circle(8 + L * 0.15, 0, 5, color, 0.16));
        }
        const ax = 9;
        parts.push(
          scene.add.rectangle(ax, 0, L * 0.55, 2.0, 0x8a7050, 1).setOrigin(0, 0.5),
          scene.add
            .rectangle(ax, -0.7, L * 0.48, 0.9, 0xc8a878, 0.7)
            .setOrigin(0, 0.5),
          scene.add.triangle(
            ax + L * 0.55,
            0,
            5,
            0,
            0,
            -2.5,
            0,
            2.5,
            shadeRgb(color, 0.85),
          ),
          scene.add.rectangle(ax - 1, -2.2, 3.5, 1.4, color, 0.9).setOrigin(0.5, 0.5),
          scene.add.rectangle(ax - 1, 2.2, 3.5, 1.4, color, 0.9).setOrigin(0.5, 0.5),
        );
      }
      break;
    }
    case "greatsword": {
      if (holy) {
        parts.push(
          scene.add.circle(-1, 0, 3.5, 0xc8a050, 1),
          scene.add.circle(-1, 0, 2.2, 0xffe08a, 1),
          scene.add.rectangle(3, 0, 10, 5.5, 0x6a5040, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(3, -1.2, 9, 2.2, 0xa88858, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(8, 0, 5, 14, 0xc8a050, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(8, 0, 3.5, 12, 0xffe08a, 1).setOrigin(0.5, 0.5),
          scene.add.triangle(8, -7, -5, 0, 5, 0, 0, -5, 0xfff6c8),
          scene.add.triangle(8, 7, -5, 0, 5, 0, 0, 5, 0xfff6c8),
        );
        addTaperBlade(scene, parts, 10, L * 0.95, L > 28 ? 6.2 : 5.2, color, true);
        parts.push(
          scene.add.circle(10 + L * 0.95 + 1, 0, 3.4, 0xffe08a, 0.95),
          scene.add.circle(10 + L * 0.95 + 1, 0, 2.0, 0xffffff, 0.85),
          scene.add.circle(10 + L * 0.95 + 1, 0, 5.5, 0x7ce8ff, 0.25),
        );
      } else if (cursed) {
        parts.push(
          scene.add.circle(-1, 0, 3.2, 0x3a2848, 1),
          scene.add.circle(-1, 0, 2.0, color, 0.85),
          scene.add.rectangle(3, 0, 10, 5.5, 0x2a2030, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(3, -1.2, 9, 2.2, 0x4a3858, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(8, 0, 5, 13, 0xd0c8b8, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(8, 0, 3.2, 11, 0xe8e0d0, 1).setOrigin(0.5, 0.5),
          scene.add.circle(8, -6.2, 2.4, 0xd0c8b8, 1),
          scene.add.circle(8, 6.2, 2.4, 0xd0c8b8, 1),
        );
        addTaperBlade(scene, parts, 10, L * 0.95, 5.4, shadeRgb(color, 0.85), true);
        const g = scene.add.graphics();
        for (let i = 0; i < 4; i++) {
          const x = 12 + i * (L * 0.18);
          g.fillStyle(0x1a1028, 1);
          g.fillRect(x, -1.2, 3, 2.4);
          g.fillStyle(color, 0.9);
          g.fillRect(x + 0.5, -0.6, 2, 1.2);
        }
        parts.push(g);
        parts.push(scene.add.circle(10 + L * 0.95 + 1, 0, 2.8, shadeRgb(color, 1.1), 0.95));
      } else {
        parts.push(
          scene.add.rectangle(2, 0, 8, 10, 0x3a3428, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(2, 0, 14, 3.5, 0x6a5848, 1).setOrigin(0.5, 0.5),
        );
        addTaperBlade(scene, parts, 8, L * 0.95, 5.2, color, true);
        parts.push(scene.add.circle(8 + L * 0.95 + 1, 0, 2.8, shadeRgb(color, 0.9), 0.95));
      }
      break;
    }
    case "turret": {
      if (cursed) {
        const g = scene.add.graphics();
        g.fillStyle(0x3a2a48, 1);
        g.fillEllipse(2, 1, 12, 10);
        g.fillStyle(0xd0c8b8, 1);
        g.fillEllipse(0, 2, 7, 6);
        g.fillStyle(0x1a1020, 1);
        g.fillCircle(-1, 1.5, 1.4);
        g.fillCircle(1.5, 1.8, 1.4);
        g.fillStyle(shadeRgb(color, 0.45), 1);
        g.fillRoundedRect(6, -4.5, L * 0.55, 9, 2);
        g.fillStyle(shadeRgb(color, 0.7), 1);
        g.fillRoundedRect(6, -3.2, L * 0.55, 6.4, 1.5);
        g.fillStyle(0x2a2038, 1);
        g.fillRect(6 + L * 0.5, -2.4, L * 0.45, 4.8);
        g.fillStyle(color, 1);
        g.fillRect(6 + L * 0.52, -1.6, L * 0.42, 3.2);
        g.fillStyle(0xffffff, 0.35);
        g.fillRect(7, -2.5, L * 0.35, 1.4);
        g.fillStyle(0x1a1028, 1);
        g.fillCircle(6 + L * 0.95, 0, 4.2);
        g.fillStyle(color, 0.85);
        g.fillCircle(6 + L * 0.95, 0, 2.4);
        if (glow) {
          g.fillStyle(color, 0.22);
          g.fillCircle(6 + L * 0.95, 0, 8);
        }
        parts.push(g);
      } else {
        addGlowRect(scene, parts, 4, 0, L, 6, color, glow);
        parts.push(
          scene.add.rectangle(2, -1, 8, 8, 0x5a5560, 1).setOrigin(0.5, 0.5),
          scene.add.rectangle(2, -1, 5, 5, 0x8a8490, 1).setOrigin(0.5, 0.5),
          scene.add
            .rectangle(4, 0, L, 5.5, shadeRgb(color, 0.55), 1)
            .setOrigin(0, 0.5),
          scene.add.rectangle(4, 0, L, 4, color, 1).setOrigin(0, 0.5),
          scene.add
            .rectangle(4 + 1, -1.2, L * 0.7, 1.6, 0xffffff, 0.35)
            .setOrigin(0, 0.5),
          scene.add
            .rectangle(4 + L * 0.72, 0, L * 0.32, 3.2, 0x2a2a30, 1)
            .setOrigin(0, 0.5),
        );
      }
      break;
    }
    case "rifle": {
      addShaft(scene, parts, 4, 0, L, 4.2, color, glow);
      parts.push(
        scene.add.rectangle(2, 0, 6, 5.5, 0x3a3a48, 1).setOrigin(0.5, 0.5),
        scene.add
          .rectangle(4 + L * 0.55, -2.8, 5, 4.5, 0x2a2a35, 1)
          .setOrigin(0, 0.5),
        scene.add
          .rectangle(4 + L - 2, 0, 6, 2.2, 0xd0d8e8, 1)
          .setOrigin(0, 0.5),
      );
      break;
    }
    case "wrench": {
      addShaft(scene, parts, 3, 0, L * 0.65, 4, 0x6a7080, glow);
      parts.push(
        scene.add
          .rectangle(3 + L * 0.52, -5, 9, 3.5, shadeRgb(color, 0.7), 1)
          .setOrigin(0, 0.5),
        scene.add
          .rectangle(3 + L * 0.52, 3, 9, 3.5, shadeRgb(color, 0.7), 1)
          .setOrigin(0, 0.5),
        scene.add
          .rectangle(3 + L * 0.55, -4, 8, 3, color, 1)
          .setOrigin(0, 0.5),
        scene.add
          .rectangle(3 + L * 0.55, 2, 8, 3, color, 1)
          .setOrigin(0, 0.5),
        scene.add.rectangle(3 + L * 0.68, -1, 7, 7, color, 1).setOrigin(0, 0.5),
        scene.add
          .rectangle(3 + L * 0.72, -2, 3, 3, 0xffffff, 0.4)
          .setOrigin(0, 0.5),
      );
      break;
    }
    case "claws": {
      if (cursed) {
        const g = scene.add.graphics();
        g.fillStyle(0x2a1838, 1);
        g.fillEllipse(2, 0, 8, 10);
        g.fillStyle(color, 0.45);
        g.fillEllipse(2, 0, 10, 12);
        const blades: { dy: number; curve: number; lenMul: number }[] = [
          { dy: -5.0, curve: -4.5, lenMul: 0.95 },
          { dy: 0.5, curve: -2.0, lenMul: 1.12 },
          { dy: 5.5, curve: 3.5, lenMul: 0.9 },
        ];
        for (const t of blades) {
          const tipX = 4 + L * t.lenMul;
          const tipY = t.dy + t.curve;
          g.fillStyle(shadeRgb(color, 0.35), 0.9);
          g.fillTriangle(3, t.dy - 2.8, 3, t.dy + 2.0, tipX + 2, tipY);
          g.fillStyle(shadeRgb(color, 0.75), 1);
          g.fillTriangle(3.5, t.dy - 1.6, 3.5, t.dy + 1.2, tipX, tipY);
          g.fillStyle(0xe8d8ff, 0.85);
          g.fillTriangle(4, t.dy - 0.8, 4, t.dy + 0.35, tipX - 2, tipY - 0.5);
          g.fillStyle(0xffffff, 0.55);
          g.fillCircle(tipX - 0.5, tipY - 0.3, 1.4);
        }
        if (glow) {
          g.fillStyle(color, 0.22);
          g.fillCircle(3 + L * 0.55, 0, 8);
        }
        parts.push(g);
      } else {
        const knuckle = 0x3a2830;
        const keratinLo = shadeRgb(color, 0.45);
        const keratinMid = shadeRgb(color, 0.75);
        const g = scene.add.graphics();
        g.fillStyle(knuckle, 1);
        g.fillEllipse(2, 0, 7, 9);
        g.fillStyle(shadeRgb(knuckle, 1.35), 0.55);
        g.fillEllipse(1.2, -1.5, 4, 4);
        const tines: { dy: number; curve: number; lenMul: number }[] = [
          { dy: -5.5, curve: -2.8, lenMul: 0.82 },
          { dy: 0, curve: -1.2, lenMul: 1.0 },
          { dy: 5.5, curve: 2.2, lenMul: 0.86 },
        ];
        for (const t of tines) {
          const tipX = 3 + L * t.lenMul;
          const tipY = t.dy + t.curve;
          g.fillStyle(keratinLo, 1);
          g.fillTriangle(3, t.dy - 2.2, 3, t.dy + 2.2, tipX + 1, tipY);
          g.fillStyle(keratinMid, 1);
          g.fillTriangle(3, t.dy - 1.4, 3, t.dy + 1.4, tipX, tipY);
          g.fillStyle(color, 1);
          g.fillTriangle(3.5, t.dy - 0.9, 3.5, t.dy + 0.35, tipX - 1, tipY - 0.4);
          g.fillStyle(0xffffff, 0.55);
          g.fillCircle(tipX - 0.5, tipY - 0.3, 1.3);
        }
        if (glow) {
          g.fillStyle(color, 0.2);
          g.fillCircle(3 + L * 0.55, 0, 7);
        }
        parts.push(g);
      }
      break;
    }
    case "staff": {
      if (holy) {
        const g = scene.add.graphics();
        g.fillStyle(0xa88840, 1);
        g.fillRect(2, -2.4, L, 4.8);
        g.fillStyle(0xf0e0a8, 1);
        g.fillRect(2, -1.7, L, 3.4);
        g.fillStyle(0xffffff, 0.75);
        g.fillRect(2, -1.5, L, 1.3);
        parts.push(g);
        addWrapBand(scene, parts, 2 + L * 0.2, 0, 5, 5.2, 0xc8a050);
        addWrapBand(scene, parts, 2 + L * 0.55, 0, 4.5, 4.8, 0x7ce8ff);
        const ox = 3 + L + 2;
        const ring = scene.add.graphics();
        ring.lineStyle(2.2, 0xffe08a, 0.95);
        ring.strokeCircle(ox, 0, 9);
        ring.lineStyle(1.4, 0xffffff, 0.7);
        ring.strokeCircle(ox, 0, 7.2);
        parts.push(ring);
        if (glow) {
          parts.push(scene.add.circle(ox, 0, 11, color, 0.22));
        }
        parts.push(
          scene.add.circle(ox, 0, 5.5, shadeRgb(color, 0.45), 1),
          scene.add.triangle(ox, 0, 0, -5.5, -4.5, 1, 4.5, 1, shadeRgb(color, 0.85)),
          scene.add.triangle(ox, 0, 0, 5.5, -4.2, 0.5, 4.2, 0.5, 0xa8fff0),
          scene.add.circle(ox, 0, 3.2, 0xffffff, 1),
          scene.add.circle(ox - 1.2, -1.4, 1.8, 0x7ce8ff, 0.85),
        );
      } else if (cursed) {
        const g = scene.add.graphics();
        g.fillStyle(0x2a2030, 1);
        g.fillRect(2, -2.6, L, 5.2);
        g.fillStyle(0xd0c8b8, 1);
        g.beginPath();
        g.moveTo(2, -2);
        g.lineTo(2 + L * 0.35, -2.8);
        g.lineTo(2 + L * 0.65, -1.2);
        g.lineTo(2 + L, -2.2);
        g.lineTo(2 + L, 2.2);
        g.lineTo(2 + L * 0.65, 2.8);
        g.lineTo(2 + L * 0.35, 1.4);
        g.lineTo(2, 2);
        g.closePath();
        g.fillPath();
        g.fillStyle(0xf0e8d8, 0.55);
        g.fillRect(2 + L * 0.1, -1.8, L * 0.75, 1.2);
        for (const t of [0.25, 0.5, 0.75]) {
          g.fillStyle(0xb8b0a0, 1);
          g.fillEllipse(2 + L * t, 0, 5, 6);
          g.fillStyle(0x3a2848, 1);
          g.fillCircle(2 + L * t - 0.5, 0, 1.2);
        }
        const ox = 3 + L + 3;
        if (L >= 26) {
          g.fillStyle(0x3a2a48, 1);
          g.fillRoundedRect(ox - 5, -7, 12, 14, 2);
          g.fillStyle(0x6a5080, 1);
          g.fillRoundedRect(ox - 4, -6, 10, 12, 1.5);
          g.fillStyle(color, 1);
          g.fillCircle(ox + 1, 0, 4.2);
          g.fillStyle(0xffffff, 0.45);
          g.fillCircle(ox, -1.5, 1.8);
          g.lineStyle(1.5, 0xd0c8b8, 1);
          g.strokeRoundedRect(ox - 5, -7, 12, 14, 2);
        } else if (L <= 18) {
          g.fillStyle(0x2a2030, 1);
          g.fillEllipse(ox, 2, 12, 8);
          g.fillStyle(shadeRgb(color, 0.55), 1);
          g.fillEllipse(ox, 1, 10, 6);
          g.fillStyle(color, 0.9);
          g.fillEllipse(ox, 0, 8, 4.5);
          g.fillStyle(0x6a3cff, 0.7);
          g.fillCircle(ox - 2, -1, 2);
          g.fillStyle(0x9bff68, 0.55);
          g.fillCircle(ox + 2, 0, 1.6);
          g.lineStyle(2, 0xd0c8b8, 1);
          g.beginPath();
          g.arc(ox - 6, 0, 3, -1.2, 1.2, false);
          g.strokePath();
          g.beginPath();
          g.arc(ox + 6, 0, 3, 2.0, 4.2, false);
          g.strokePath();
        } else {
          g.fillStyle(0x4a3858, 1);
          g.fillTriangle(ox - 2, 3, ox + 10, -8, ox + 4, 4);
          g.fillStyle(color, 1);
          g.fillCircle(ox + 6, -5, 4);
          g.fillStyle(0xffffff, 0.4);
          g.fillCircle(ox + 5, -6, 1.6);
          g.fillStyle(0xd0c8b8, 1);
          g.fillTriangle(ox + 4, -9, ox + 7, -14, ox + 9, -8);
        }
        if (glow) {
          g.fillStyle(color, 0.2);
          g.fillCircle(ox, 0, 10);
        }
        parts.push(g);
      } else {
        const wood = 0x6a5040;
        const woodHi = 0x9a7858;
        const woodLo = 0x3a2a22;
        const g = scene.add.graphics();
        g.fillStyle(woodLo, 1);
        g.fillRect(2, -2.4, L, 4.8);
        g.fillStyle(wood, 1);
        g.fillRect(2, -1.7, L, 3.4);
        g.fillStyle(woodHi, 0.85);
        g.fillRect(2, -1.5, L, 1.3);
        parts.push(g);
        addWrapBand(scene, parts, 2 + L * 0.18, 0, 5, 5.2, 0x4a3a60);
        addWrapBand(scene, parts, 2 + L * 0.55, 0, 4.5, 4.8, 0x5a4840);
        parts.push(
          scene.add
            .rectangle(2 + L - 3, 0, 5, 6.5, 0x6a7088, 1)
            .setOrigin(0, 0.5),
          scene.add
            .rectangle(2 + L - 2.5, -1.5, 4, 2, 0xc0c8d8, 0.55)
            .setOrigin(0, 0.5),
        );
        const ox = 3 + L + 2;
        if (glow) {
          parts.push(scene.add.circle(ox, 0, 10, color, 0.2));
        }
        parts.push(
          scene.add.circle(ox, 0, 6.2, shadeRgb(color, 0.4), 1),
          scene.add.triangle(ox, 0, 0, -5.5, -4.5, 1, 4.5, 1, shadeRgb(color, 0.7)),
          scene.add.triangle(ox, 0, 0, 5.5, -4.2, 0.5, 4.2, 0.5, shadeRgb(color, 0.55)),
          scene.add.circle(ox, 0, 3.6, color, 1),
          scene.add.circle(ox - 1.4, -1.6, 2.0, 0xffffff, 0.7),
          scene.add.circle(ox + 1.2, 1.4, 1.2, shadeRgb(color, 0.35), 0.8),
        );
      }
      break;
    }
    case "blade":
    case "sword":
    default: {
      const gripWood = 0x3a2a20;
      const gripMid = 0x6a5040;
      const guard = holy ? 0xffe08a : cursed ? 0x8a70ff : 0xc8a858;
      const guardLo = holy ? 0xa88840 : cursed ? 0x3a2848 : 0x6a5840;
      const steel = color;
      parts.push(
        scene.add.circle(-2, 0, 3.2, guardLo, 1),
        scene.add.circle(-2, 0, 2.2, guard, 1),
        scene.add.circle(-2.6, -0.8, 1.1, 0xfff0c0, 0.65),
      );
      parts.push(
        scene.add.rectangle(2, 0, 9, 5.5, gripWood, 1).setOrigin(0.5, 0.5),
        scene.add.rectangle(2, -1.2, 8, 2.2, gripMid, 1).setOrigin(0.5, 0.5),
        scene.add.rectangle(0.5, 0, 2, 5.5, 0x2a2018, 1).setOrigin(0.5, 0.5),
        scene.add.rectangle(3.5, 0, 2, 5.5, 0x2a2018, 1).setOrigin(0.5, 0.5),
      );
      parts.push(
        scene.add.rectangle(7, 0, 4.5, 11, guardLo, 1).setOrigin(0.5, 0.5),
        scene.add.rectangle(7, 0, 3.2, 9.5, guard, 1).setOrigin(0.5, 0.5),
        scene.add.rectangle(7, -3.5, 2.4, 2.2, 0xffe8a0, 0.55).setOrigin(0.5, 0.5),
        scene.add.circle(7, -5.2, 2.0, guard, 1),
        scene.add.circle(7, 5.2, 2.0, guard, 1),
      );
      addTaperBlade(scene, parts, 9, L * 0.92, 4.4, steel, glow);
      parts.push(
        scene.add.circle(9 + L * 0.92 + 0.5, 0, 2.2, shadeRgb(steel, 1.1), 0.9),
        scene.add.circle(9 + L * 0.92 + 0.2, -0.6, 1.0, 0xffffff, 0.55),
      );
      break;
    }
  }
  return parts;
}

/** Heater or tower shield — holy gold/white or steel default. */
function buildShieldParts(
  scene: Phaser.Scene,
  style: ShieldStyle = "heater",
  skin: WeaponSkin = "default",
): Phaser.GameObjects.Container {
  const g = scene.add.graphics();
  const holy = skin === "holy";
  const rim = holy ? 0xa88840 : 0x2a3040;
  const mid = holy ? 0xf0e8c8 : 0x5a7088;
  const lit = holy ? 0xffffff : 0x8aa0b8;
  const boss = holy ? 0xffe08a : 0xc8a858;
  const bossHi = holy ? 0x7ce8ff : 0xffe8a0;
  if (style === "tower") {
    const w = 14;
    const h = 22;
    g.fillStyle(rim, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, 3);
    g.fillStyle(mid, 1);
    g.fillRoundedRect(-w / 2 + 1.5, -h / 2 + 1.5, w - 3, h - 3, 2);
    g.fillStyle(lit, 1);
    g.fillRoundedRect(-w / 2 + 3, -h / 2 + 3, w * 0.45, h - 6, 1.5);
    g.fillStyle(boss, 1);
    g.fillCircle(0.5, -2, 3.2);
    g.fillStyle(bossHi, 0.75);
    g.fillCircle(-0.3, -2.8, 1.5);
    g.fillStyle(rim, 0.85);
    g.fillRect(-1, -h / 2 + 2, 2, h - 4);
  } else {
    g.fillStyle(rim, 1);
    g.fillEllipse(0, 0, 15, 18);
    g.fillStyle(mid, 1);
    g.fillEllipse(0, 0, 12.5, 15.5);
    g.fillStyle(lit, 1);
    g.fillEllipse(-1.2, -1.5, 9.5, 11.5);
    g.fillStyle(holy ? 0x7ce8ff : 0xe8f0ff, 0.45);
    g.fillEllipse(-2.2, -3.2, 4.5, 5.5);
    g.fillStyle(boss, 1);
    g.fillCircle(0.5, 0.5, 2.8);
    g.fillStyle(bossHi, 0.7);
    g.fillCircle(-0.2, -0.3, 1.4);
    g.lineStyle(1.6, holy ? 0xffe08a : 0xd0dce8, 0.75);
    g.beginPath();
    g.arc(-1, -2, 6.5, -2.2, -0.4, false);
    g.strokePath();
  }
  return scene.add.container(style === "tower" ? -10 : -8, style === "tower" ? 1 : 2, [g]);
}

/**
 * Simple spine: shadow + body + feet + hand + weapon(+shield) under a container.
 */
export class UnitRig {
  readonly container: Phaser.GameObjects.Container;
  readonly body: Phaser.GameObjects.Image;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly footL: Phaser.GameObjects.Ellipse;
  private readonly footR: Phaser.GameObjects.Ellipse;
  private readonly hand: Phaser.GameObjects.Ellipse;
  private readonly weapon: Phaser.GameObjects.Container;
  private readonly shield: Phaser.GameObjects.Container | null;
  private readonly cfg: RigRoleConfig;
  private readonly walkEnter: number;
  private readonly roleId: string;
  private readonly gripPack: GripPack | null;
  private trailCd = 0;
  /** Smoothed aim for pose; null until first live update (snap). */
  private displayAim: number | null = null;
  private footSide = 1;
  /** Cached weapon depth mode — only when facingDir is explicit (Dropfront). */
  private lastWeaponBehind: boolean | null = null;
  /** Last grip+rot from `update` (for muzzle world tip). */
  private lastGx = 0;
  private lastGy = 0;
  private lastRot = 0;
  /** Body display size when the rig was built (tier scale 1 baseline). */
  private readonly refDisplaySize: number;
  private lastPropScale = 1;
  private lastShadowKey = "";
  /** When true, `setPartsVisible` must not re-show the embedded foot-shadow. */
  private shadowForcedHidden = false;
  /** Table default; restored after skill pose overrides (e.g. shield bash). */
  private readonly baseAttackStyle: AttackStyle;

  constructor(
    scene: Phaser.Scene,
    body: Phaser.GameObjects.Image,
    role: string,
    tables: RigTables | undefined,
    walkEnter: number,
    bladeColorOverride?: number,
    gripPack?: GripPack | null,
  ) {
    this.roleId = role;
    this.gripPack = gripPack ?? null;
    this.cfg = resolveRigConfig(tables, role);
    this.baseAttackStyle = this.cfg.attackStyle;
    if (bladeColorOverride != null && Number.isFinite(bladeColorOverride)) {
      this.cfg.bladeColor = bladeColorOverride;
    }
    this.walkEnter = walkEnter;
    this.body = body;
    this.refDisplaySize = Math.max(
      8,
      body.displayWidth || body.width || 16,
    );

    const sh0 = shadowEllipseFromDisplay(
      this.refDisplaySize,
      this.cfg.shadowRx,
      this.cfg.shadowRy,
      {
        shadowOffsetY: this.cfg.shadowOffsetY,
        footBiasFrac: this.cfg.footBiasFrac,
      },
    );
    this.shadow = scene.add.ellipse(
      0,
      sh0.y,
      sh0.rx * 2,
      sh0.ry * 2,
      0x000000,
      this.cfg.shadowAlpha,
    );
    this.lastShadowKey = `${sh0.rx.toFixed(2)}:${sh0.ry.toFixed(2)}:${sh0.y.toFixed(2)}`;

    this.footL = scene.add.ellipse(-4, 6, 5, 3.5, 0xf0f4ff, 0.95);
    this.footR = scene.add.ellipse(4, 6, 5, 3.5, 0xf0f4ff, 0.95);
    this.footL.setVisible(this.cfg.showFeet);
    this.footR.setVisible(this.cfg.showFeet);

    this.body.setVisible(!this.cfg.hideBody);

    this.hand = scene.add.ellipse(0, 0, 6, 6, 0xf5f7ff, 1);
    this.hand.setVisible(this.cfg.showHand);

    this.weapon = scene.add.container(
      0,
      0,
      buildWeaponParts(
        scene,
        this.cfg.weaponShape,
        this.cfg.weaponLength,
        this.cfg.bladeColor,
        this.cfg.glow,
        this.cfg.weaponSkin,
      ),
    );
    this.weapon.setVisible(this.cfg.showWeapon);

    this.shield = this.cfg.showShield
      ? buildShieldParts(scene, this.cfg.shieldStyle, this.cfg.weaponSkin)
      : null;

    // Shield above weapon so bash thrust stays readable.
    const kids: Phaser.GameObjects.GameObject[] = [
      this.shadow,
      this.footL,
      this.footR,
      this.body,
      this.weapon,
      this.hand,
    ];
    if (this.shield) kids.push(this.shield);
    this.container = scene.add.container(0, 0, kids);
  }

  setBodyTexture(key: string): void {
    if (this.body.texture.key !== key) this.body.setTexture(key);
  }

  setPartsVisible(visible: boolean): void {
    this.shadow.setVisible(visible && !this.shadowForcedHidden);
    this.footL.setVisible(visible && this.cfg.showFeet);
    this.footR.setVisible(visible && this.cfg.showFeet);
    this.hand.setVisible(visible && this.cfg.showHand);
    this.weapon.setVisible(visible && this.cfg.showWeapon);
    this.shield?.setVisible(visible && this.cfg.showShield);
    if (!this.cfg.hideBody) this.body.setVisible(visible);
  }

  /** Hide embedded foot-shadow while a separate ground ellipse tracks feet. */
  setShadowVisible(visible: boolean): void {
    this.shadowForcedHidden = !visible;
    this.shadow.setVisible(visible);
  }

  update(
    u: UnitRigUpdate,
    trailBudget: TrailBudget,
    scene: Phaser.Scene,
    rootWorld: { x: number; y: number },
  ): void {
    if (u.dying) {
      this.displayAim = 0;
      this.setPartsVisible(false);
      this.body.setVisible(true);
      return;
    }

    this.setPartsVisible(true);
    if (this.cfg.hideBody) this.body.setVisible(false);

    const dt = 1 / 30;
    const turnRate =
      u.animState === "walk"
        ? this.cfg.displayAimTurnRate * 0.55
        : this.cfg.displayAimTurnRate;
    if (this.displayAim == null) {
      this.displayAim = u.aimAngle;
    } else {
      this.displayAim = stepDisplayAim(
        this.displayAim,
        u.aimAngle,
        turnRate,
        dt,
      );
    }
    const aim = this.displayAim;
    this.footSide = footSideFromAim(
      aim,
      this.footSide,
      this.cfg.footSideHoldCos,
    );

    const bob = bodyBobY(u.animState, u.animTime, this.cfg.bobAmp, this.cfg.bobHz);
    if (!scene.tweens.isTweening(this.body)) {
      this.body.setY(bob);
    }
    this.body.setFlipX(u.lockBodyFlip ? false : u.facing < 0);

    const feet = footPhaseOffsets(u.animState, u.animTime, this.cfg.bobHz);
    const side = this.footSide;
    this.footL.setPosition(-4 * side, 6 + feet.left * 0.15);
    this.footR.setPosition(4 * side, 6 + feet.right * 0.15);
    this.footL.y += bob * 0.25;
    this.footR.y += bob * 0.25;

    const motion = attackMotion(
      u.animState,
      u.animTime,
      this.cfg.attackStyle,
      this.cfg.swingAmp,
      this.cfg.swingSec,
    );
    const displaySize = Math.max(
      8,
      this.body.displayWidth || this.body.width || 16,
    );
    const propScale = propScaleFromBodyDisplay(
      displaySize,
      this.refDisplaySize,
    );
    if (Math.abs(propScale - this.lastPropScale) > 0.001) {
      this.weapon.setScale(propScale);
      this.hand.setScale(propScale);
      this.shield?.setScale(propScale);
      this.lastPropScale = propScale;
    }
    const sh = shadowEllipseFromDisplay(
      displaySize,
      this.cfg.shadowRx,
      this.cfg.shadowRy,
      {
        shadowOffsetY: this.cfg.shadowOffsetY,
        footBiasFrac: this.cfg.footBiasFrac,
      },
    );
    const shadowKey = `${sh.rx.toFixed(2)}:${sh.ry.toFixed(2)}:${sh.y.toFixed(2)}`;
    if (shadowKey !== this.lastShadowKey) {
      this.shadow.setSize(sh.rx * 2, sh.ry * 2);
      this.shadow.setPosition(0, sh.y);
      this.shadow.setFillStyle(0x000000, this.cfg.shadowAlpha);
      this.lastShadowKey = shadowKey;
    }
    const slotted = resolveGripLocal(
      this.gripPack ?? undefined,
      this.roleId,
      u.facingDir,
      u.animState,
      u.animTime,
      displaySize,
    );

    let gx: number;
    let gy: number;
    let rot: number;
    if (slotted) {
      // Grip SSOT drives hand slot; attackMotion supplies full blade sweep.
      gx = slotted.x;
      gy = slotted.y;
      rot = aim + this.cfg.readyOffset + motion.rot;
      gx += Math.cos(aim) * motion.extend * propScale;
      gy += Math.sin(aim) * motion.extend * propScale;
    } else {
      rot = aim + this.cfg.readyOffset + motion.rot;
      const grip = gripLocalPos(this.cfg.gripOffset, this.cfg.readyOffset);
      gx = grip.x * Math.cos(aim) - grip.y * Math.sin(aim);
      gy = grip.x * Math.sin(aim) + grip.y * Math.cos(aim);
      gx += Math.cos(aim) * motion.extend * propScale;
      gy += Math.sin(aim) * motion.extend * propScale;
    }
    // Walk: almost no weapon Y bob — grip rotation was the main tremble channel.
    // Slotted grip already encodes hand motion in SSOT; do not add procedural bob.
    const wBobMul = slotted
      ? 0
      : u.animState === "walk"
        ? this.cfg.weaponBobMul * 0.25
        : this.cfg.weaponBobMul;
    const wBob = bob * wBobMul;
    this.weapon.setPosition(gx, gy + wBob);
    this.weapon.setRotation(rot);
    this.lastGx = gx;
    this.lastGy = gy + wBob;
    this.lastRot = rot;
    this.syncWeaponLayerOrder(u.facingDir);

    if (this.shield) {
      // Guard: always visible, slightly forward/side toward aim (not behind).
      // Bash: ride attackMotion.extend so the shield lunges as the strike.
      const guardAlong = 5 * propScale;
      const guardSide = 7 * propScale;
      const bash =
        u.animState === "attack" && this.cfg.attackStyle === "bash";
      const thrustMul = bash ? 1.35 : 0.45;
      const along =
        guardAlong + motion.extend * thrustMul * propScale;
      const side = guardSide + (bash ? motion.rot * 2.5 * propScale : 0);
      const sx =
        Math.cos(aim) * along + Math.sin(aim) * side;
      const sy =
        Math.sin(aim) * along -
        Math.cos(aim) * side +
        bob * 0.2;
      this.shield.setPosition(sx, sy);
      // Face plate toward aim; add strike tilt during bash.
      this.shield.setRotation(aim + (bash ? motion.rot * 0.4 : 0));
    }

    const handR = Math.max(2, this.cfg.weaponLength * 0.08) * propScale;
    this.hand.setPosition(
      gx + Math.cos(rot) * handR,
      gy + wBob + Math.sin(rot) * handR,
    );

    const spd = Math.hypot(u.intentVx, u.intentVy);
    this.trailCd -= dt;
    if (
      this.cfg.trailEnabled &&
      trailBudget.left > 0 &&
      this.trailCd <= 0 &&
      trailSpawnChance(spd, this.walkEnter)
    ) {
      trailBudget.left -= 1;
      this.trailCd = this.cfg.moveDustCdSec;
      const dustScale = moveDustScaleFromDisplay(displaySize, {
        scaleMul: this.cfg.moveDustScaleMul,
        scaleMax: this.cfg.moveDustScaleMax,
      });
      const spread = this.cfg.moveDustSpread;
      // Local to the unit container so Sim Lab lane offsetX is inherited
      // (world-space spawn without ox painted B's dust onto lane A).
      const lx = -Math.cos(aim) * (6 + dustScale * 2) * spread;
      const ly = sh.y - Math.sin(aim) * (4 + dustScale) * spread;
      spawnMoveDust({
        scene,
        x: lx,
        y: ly,
        scale: dustScale,
        alpha: this.cfg.moveDustAlpha,
        parent: this.container,
      });
    }
  }

  /** World tip of weapon / muzzle along blade axis (after last `update`). */
  muzzleWorld(root: { x: number; y: number }): { x: number; y: number } {
    const tip = muzzleLocal(
      this.lastGx,
      this.lastGy,
      this.lastRot,
      this.cfg.weaponLength * this.lastPropScale,
    );
    return { x: root.x + tip.x, y: root.y + tip.y };
  }

  /**
   * North facing: weapon under body (Dropfront cardinal kits only).
   * Skip when facingDir omitted (Portal FFA) — per-frame moveTo from continuous
   * aim was thrashing container order and breaking combat presentation.
   */
  private syncWeaponLayerOrder(facingDir: CardinalDir | undefined): void {
    if (facingDir == null) {
      if (this.lastWeaponBehind != null) {
        this.applyDefaultLayerOrder();
        this.lastWeaponBehind = null;
      }
      return;
    }
    const behind = weaponDrawnBehindBody(facingDir);
    if (this.lastWeaponBehind === behind) return;
    this.lastWeaponBehind = behind;
    if (behind) this.applyBehindLayerOrder();
    else this.applyDefaultLayerOrder();
  }

  private applyDefaultLayerOrder(): void {
    // Shield above weapon so bash thrust stays readable (not buried under blade).
    const order: Phaser.GameObjects.GameObject[] = [
      this.shadow,
      this.footL,
      this.footR,
      this.body,
      this.weapon,
      this.hand,
    ];
    if (this.shield) order.push(this.shield);
    for (let i = 0; i < order.length; i++) {
      this.container.moveTo(order[i]!, i);
    }
  }

  private applyBehindLayerOrder(): void {
    const order: Phaser.GameObjects.GameObject[] = [
      this.shadow,
      this.footL,
      this.footR,
      this.weapon,
    ];
    if (this.cfg.showHand) order.push(this.hand);
    order.push(this.body);
    if (this.shield) order.push(this.shield);
    if (!this.cfg.showHand) order.push(this.hand);
    for (let i = 0; i < order.length; i++) {
      this.container.moveTo(order[i]!, i);
    }
  }

  setAttackStyle(style: AttackStyle): void {
    this.cfg.attackStyle = style;
  }

  /** Restore attackStyle from rig table resolve (after skill override). */
  restoreAttackStyle(): void {
    this.cfg.attackStyle = this.baseAttackStyle;
  }

  destroy(): void {
    this.container.destroy(true);
  }
}
